import React, {
  useEffect, useReducer, useRef, ChangeEvent,
} from 'react';
import { RiFlagLine } from 'react-icons/ri';
import { useIntl } from 'react-intl';
import reportButtonMessages from './messages';

import './ReportButton.scss';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');
const {
  useToggle, Button, ModalPopup, ModalCloseButton, Form, Spinner,
} = require('@edx/paragon');

interface Action {
  type: string;
  target: HTMLButtonElement | null;
  reason: string | null;
  feedBack: string | null;
}

interface State {
  target: HTMLButtonElement | null;
  reason: string | null;
  feedBack: string | null;
  isLoading: boolean;
}

const initialState: State = {
  target: null,
  reason: null,
  feedBack: null,
  isLoading: true,
};

/**
 * Reducer function that manages the state of the feedback form.
 *
 * @param {State} state - The current state of the feedback form.
 * @param {Action} action - The action that should be performed on the state.
 *
 * @returns {State} - The updated state of the feedback form.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_TARGET':
      return { ...state, isLoading: false, target: action.target };
    case 'SET_REASON':
      return { ...state, isLoading: false, reason: action.reason };
    case 'SET_FEEDBACK':
      return { ...state, isLoading: false, feedBack: action.feedBack };
    case 'LOADING':
      return { ...state, isLoading: true };
    default:
      return state;
  }
}

/**
 *  Sets a report for a course unit given its course and unit IDs and a reason for reporting.
 * @param {React.Dispatch<Action>} dispatch - The dispatch function of the React state.
 * @param {string} courseId - The ID of the course.
 * @param {string} unitId - The ID of the course unit.
 * @param {string} reason - The reason for reporting the unit.
 * @returns {void}
 */
// eslint-disable-next-line max-len
function setReport(dispatch: React.Dispatch<Action>, courseId: string, unitId: string, reason: string | null, successMessage: string, errorMessage:string) {
  const url = `${getConfig().COURSE_EXPERIENCE_API_URL}/report/units/`;
  const body = {
    reason,
    item_id: unitId,
    course_id: { type: 'CourseOverview', id: courseId },
  };
  const headers = { headers: { accept: 'application/vnd.api+json,application/json' } };

  dispatch({
    type: 'LOADING',
    feedBack: null,
    reason: null,
    target: null,
  });

  getAuthenticatedHttpClient().post(url, body, headers).then(() => {
    dispatch({
      type: 'SET_FEEDBACK',
      feedBack: successMessage,
      reason: null,
      target: null,
    });
  }, (error: Error) => {
    dispatch({
      type: 'SET_FEEDBACK',
      feedBack: errorMessage,
      reason: null,
      target: null,
    });
    // eslint-disable-next-line no-console
    console.error('Unable to set like-dislike status with error:', error.message);
  });
}

/**
 * Get a report based on the given course id and unit id.
 *
 * @param {React.Dispatch<Action>} dispatch - The dispatch function to update the state of the report button.
 * @param {string} courseId - The ID of the course containing the unit.
 * @param {string} unitId - The ID of the unit being reported.
 * @returns {void}
 */
function getReport(dispatch: React.Dispatch<Action>, unitId: string, savedMessage:string) {
  const url = `${getConfig().COURSE_EXPERIENCE_API_URL}/report/units/${unitId}/`;
  const headers = { headers: { accept: 'application/vnd.api+json,application/json' } };

  dispatch({
    type: 'LOADING',
    feedBack: null,
    reason: null,
    target: null,
  });

  getAuthenticatedHttpClient().get(url, headers).then(() => {
    dispatch({
      type: 'SET_FEEDBACK',
      feedBack: savedMessage,
      reason: null,
      target: null,
    });
  }, () => {
    dispatch({
      type: 'SET_FEEDBACK',
      feedBack: null,
      reason: null,
      target: null,
    });
  });
}

/**
 * A button that opens a modal popup for reporting inappropriate content.
 *
 * @param {string} unitId - The ID of the unit being reported.
 * @param {string} courseId - The ID of the course containing the unit.
 * @returns {JSX.Element} A React component representing the report button.
 */
function ReportButton({ unitId, courseId }: { unitId: string, courseId: string }): JSX.Element {
  const [isOpen, open, close] = useToggle(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const intl = useIntl();

  const {
    feedBack, isLoading, reason, target,
  } = state;

  useEffect(() => {
    dispatch({
      type: 'SET_TARGET',
      target: buttonRef?.current,
      feedBack: null,
      reason: null,
    });
  }, [buttonRef]);

  useEffect(() => {
    if (isOpen) {
      getReport(dispatch, unitId, intl.formatMessage(reportButtonMessages.savedMessage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unitId]);

  return (
    <>
      <div className="report-container">
        <Button
          ref={buttonRef}
          variant="outline-primary"
          iconAfter={RiFlagLine}
          size="sm"
          onClick={open}
          className="report-button"
          id="report-button"
        >
          {intl.formatMessage(reportButtonMessages.report)}
        </Button>
      </div>
      <ModalPopup
        hasArrow
        placement="right"
        positionRef={target}
        isOpen={isOpen}
        onClose={close}
      >
        <div
          className="bg-white p-3 rounded shadow border pop-up"
          style={{ textAlign: 'start' }}
        >

          <Form.Group>
            <Form.Label>{intl.formatMessage(reportButtonMessages.formTitle)}</Form.Label>
            {isLoading && (
              <div className="loading-container">
                <Spinner animation="grow" variant="primary" screenReaderText="loading" />
              </div>
            )}
            {!isLoading && feedBack && (
              <div className="report-feedback-container">{feedBack}</div>
            )}
            {!isLoading && !feedBack && (
              <Form.RadioSet
                name="report-reasons"
                onChange={(element: ChangeEvent<HTMLButtonElement>) => {
                  dispatch({
                    type: 'SET_REASON',
                    reason: element.target.value,
                    target: null,
                    feedBack: null,
                  });
                }}
                value={reason}
              >
                <Form.Radio className="mt-2" value="SC">{intl.formatMessage(reportButtonMessages.sexualContent)}</Form.Radio>
                <Form.Radio value="GV">{intl.formatMessage(reportButtonMessages.graphicViolence)}</Form.Radio>
                <Form.Radio value="HA">{intl.formatMessage(reportButtonMessages.hatefulOrAbusiveContent)}</Form.Radio>
                <Form.Radio value="CI">{intl.formatMessage(reportButtonMessages.copycatOrImpersonation)}</Form.Radio>
                <Form.Radio className="mb-2" value="OO">{intl.formatMessage(reportButtonMessages.otherObjection)}</Form.Radio>
              </Form.RadioSet>
            )}
          </Form.Group>

          <Button
            variant="primary"
            className="mie-2 test"
            onClick={() => {
              setReport(
                dispatch,
                courseId,
                unitId,
                reason,
                intl.formatMessage(reportButtonMessages.successMessage),
                intl.formatMessage(reportButtonMessages.errorMessage),
              );
            }}
            size="sm"
            disabled={isLoading || feedBack}
            id="save-button"
          >
            {intl.formatMessage(reportButtonMessages.save)}
          </Button>
          <ModalCloseButton variant="outline-primary" size="sm" disabled={isLoading}>{intl.formatMessage(reportButtonMessages.close)}</ModalCloseButton>
        </div>
      </ModalPopup>
    </>
  );
}

export default ReportButton;
